import { useState } from "react";
import { Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { useConceptList } from "@/hooks/useConcepts";
import {
  useComputeOverlap,
  useAnalyzeOverlap,
} from "@/hooks/useConceptOverlap";
import { useCurrentUser, isPaidPlan } from "@/hooks/useCurrentUser";
import { UpgradeCard } from "@/components/UpgradeCard";
import { VennTwo, VennThree } from "@/components/concepts/VennDiagram";
import type { ConceptListItem } from "@/types/concept";
import type {
  OverlapResponse,
  OverlapAnalysisResult,
} from "@/types/overlap";

// ── Concept Selector ────────────────────────────────────────────────

function ConceptSelector({
  concepts,
  selectedIds,
  onToggle,
}: {
  concepts: ConceptListItem[];
  selectedIds: string[];
  onToggle: (id: string) => void;
}) {
  return (
    <div
      className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4"
      data-testid="concept-selector"
    >
      {concepts.map((c) => {
        const selected = selectedIds.includes(c.id);
        return (
          <button
            key={c.id}
            onClick={() => onToggle(c.id)}
            className={`rounded-lg border p-3 text-left transition-all ${
              selected
                ? "ring-2 ring-primary border-primary bg-primary/5"
                : "hover:border-muted-foreground/30"
            }`}
            data-testid={`concept-card-${c.id}`}
          >
            <p className="text-sm font-medium truncate">{c.name}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {(c.foundational_count ?? 0) + (c.trawled_count ?? 0)} papers
            </p>
          </button>
        );
      })}
    </div>
  );
}

// ── Overlap Stats ───────────────────────────────────────────────────

function OverlapStats({ data }: { data: OverlapResponse }) {
  const conceptNames = data.concepts.map((c) => c.concept_name);

  return (
    <div className="space-y-4" data-testid="overlap-stats">
      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {data.concepts.map((c) => (
          <Card key={c.concept_id}>
            <CardContent className="p-3">
              <p className="text-xs text-muted-foreground truncate">
                {c.concept_name}
              </p>
              <p className="text-lg font-semibold">{c.total_papers}</p>
              <p className="text-xs text-muted-foreground">
                {c.unique_papers} unique
              </p>
            </CardContent>
          </Card>
        ))}
        <Card>
          <CardContent className="p-3">
            <p className="text-xs text-muted-foreground">Shared</p>
            <p className="text-lg font-semibold">{data.total_shared}</p>
            <p className="text-xs text-muted-foreground">papers</p>
          </CardContent>
        </Card>
      </div>

      {/* Venn diagram */}
      <div className="flex justify-center">
        {data.concepts.length === 2 && data.pairwise.length === 1 && (
          <VennTwo pair={data.pairwise[0]} />
        )}
        {data.concepts.length === 3 && (
          <VennThree
            pairs={data.pairwise}
            tripleOverlaps={data.triple_overlaps}
            conceptNames={conceptNames}
          />
        )}
        {data.concepts.length === 4 && (
          <div className="grid grid-cols-3 gap-4">
            {data.pairwise.map((pw, i) => (
              <div key={i} className="text-center">
                <VennTwo pair={pw} width={220} height={150} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Jaccard badges */}
      <div className="flex flex-wrap gap-2">
        {data.pairwise.map((pw, i) => (
          <Badge key={i} variant="outline" data-testid="jaccard-badge">
            {pw.concept_a_name} ∩ {pw.concept_b_name}: J=
            {pw.jaccard.toFixed(2)}
          </Badge>
        ))}
      </div>
    </div>
  );
}

// ── Shared Papers Table ─────────────────────────────────────────────

function SharedPapersTable({ data }: { data: OverlapResponse }) {
  if (data.shared_papers.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="shared-papers-table">
      <h3 className="text-sm font-semibold">
        Shared Papers ({data.total_shared})
      </h3>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="p-2 text-left font-medium">Title</th>
              <th className="p-2 text-left font-medium w-16">Year</th>
              <th className="p-2 text-left font-medium w-20">Citations</th>
              <th className="p-2 text-left font-medium">Concepts</th>
            </tr>
          </thead>
          <tbody>
            {data.shared_papers.map((sp) => (
              <tr key={sp.paper_id} className="border-b last:border-0">
                <td className="p-2 max-w-xs truncate" title={sp.title}>
                  {sp.title}
                </td>
                <td className="p-2 text-muted-foreground">{sp.year ?? "—"}</td>
                <td className="p-2 text-muted-foreground">{sp.citations}</td>
                <td className="p-2">
                  <div className="flex flex-wrap gap-1">
                    {sp.concept_appearances.map((a) => (
                      <Badge
                        key={a.concept_id}
                        variant="secondary"
                        className="text-xs"
                      >
                        {a.concept_name}
                        {a.relevance_score > 0 && (
                          <span className="ml-1 opacity-60">
                            ({a.relevance_score})
                          </span>
                        )}
                      </Badge>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Analysis Panel ──────────────────────────────────────────────────

function OverlapAnalysisPanel({ data }: { data: OverlapAnalysisResult }) {
  if (data.error) {
    return (
      <div
        className="rounded-md border border-destructive p-4"
        data-testid="analysis-error"
      >
        <p className="text-sm text-destructive">
          Analysis failed: {data.error}
        </p>
      </div>
    );
  }

  return (
    <div
      className="space-y-4 rounded-md border border-dashed bg-muted/30 p-4"
      data-testid="overlap-analysis-panel"
    >
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Sparkles className="size-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">AI Connections</h3>
          <Badge variant="outline" className="text-[10px]">
            suggestions
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          AI-generated from the {data.shared_paper_count} shared papers across{" "}
          {data.concept_count} concepts — directions to consider, not verified
          findings. Check against the sources before relying on them.
        </p>
      </div>

      {/* Synthesis */}
      {data.synthesis && (
        <>
          <p className="text-sm leading-relaxed">{data.synthesis}</p>
          <Separator />
        </>
      )}

      {/* Concept Relationships */}
      {data.concept_relationships.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Concept Relationships
          </h4>
          {data.concept_relationships.map((rel, i) => (
            <Card key={i}>
              <CardContent className="space-y-2 pt-4 text-xs">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{rel.concept_a}</Badge>
                  <span className="text-muted-foreground">↔</span>
                  <Badge variant="secondary">{rel.concept_b}</Badge>
                  <Badge variant="outline" className="font-normal">
                    suggests: {rel.relationship_type}
                  </Badge>
                </div>
                <p>{rel.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Bridge Papers */}
      {data.bridge_papers.length > 0 && (
        <div className="space-y-3">
          <Separator />
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Bridge Papers
          </h4>
          {data.bridge_papers.map((bp, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{bp.paper_title}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-xs">
                <div className="flex flex-wrap gap-1">
                  {bp.concepts_bridged.map((c) => (
                    <Badge key={c} variant="secondary">
                      {c}
                    </Badge>
                  ))}
                </div>
                <p>{bp.bridge_rationale}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Cross-Concept Gaps */}
      {data.cross_concept_gaps.length > 0 && (
        <div className="space-y-3">
          <Separator />
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Cross-Concept Gaps
          </h4>
          {data.cross_concept_gaps.map((gap, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{gap.title}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-xs">
                <p>{gap.description}</p>
                <div className="flex flex-wrap gap-1">
                  {gap.relevant_concepts.map((c) => (
                    <Badge key={c} variant="secondary">
                      {c}
                    </Badge>
                  ))}
                </div>
                {gap.suggested_directions.length > 0 && (
                  <div>
                    <span className="font-medium">Directions:</span>
                    <ul className="ml-4 mt-1 list-disc">
                      {gap.suggested_directions.map((d, j) => (
                        <li key={j}>{d}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Cross-Pollination */}
      {data.cross_pollination.length > 0 && (
        <div className="space-y-3">
          <Separator />
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Cross-Pollination Opportunities
          </h4>
          {data.cross_pollination.map((cp, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{cp.title}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-xs">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{cp.from_concept}</Badge>
                  <span className="text-muted-foreground">→</span>
                  <Badge variant="secondary">{cp.to_concept}</Badge>
                </div>
                <p>{cp.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────

export function OverlapAnalysis() {
  const { data: concepts, isLoading: conceptsLoading } = useConceptList();
  const { data: currentUser } = useCurrentUser();
  const paid = isPaidPlan(currentUser?.plan);
  const computeMutation = useComputeOverlap();
  const analyzeMutation = useAnalyzeOverlap();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [overlapData, setOverlapData] = useState<OverlapResponse | null>(null);
  const [analysisData, setAnalysisData] =
    useState<OverlapAnalysisResult | null>(null);

  const handleToggle = (id: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((x) => x !== id);
      }
      if (prev.length >= 4) return prev; // Max 4
      return [...prev, id];
    });
    // Reset results when selection changes
    setOverlapData(null);
    setAnalysisData(null);
  };

  const handleCompare = () => {
    computeMutation.mutate(
      { concept_ids: selectedIds },
      {
        onSuccess: (data) => {
          setOverlapData(data);
          setAnalysisData(null);
        },
      },
    );
  };

  const handleAnalyze = () => {
    analyzeMutation.mutate(
      { concept_ids: selectedIds },
      {
        onSuccess: (data) => setAnalysisData(data),
      },
    );
  };

  if (conceptsLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  if (!concepts || concepts.length < 2) {
    return (
      <p className="text-sm text-muted-foreground">
        You need at least 2 concepts to compare.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      {/* Concept selector */}
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Select 2-4 concepts to compare ({selectedIds.length} selected)
        </p>
        <ConceptSelector
          concepts={concepts}
          selectedIds={selectedIds}
          onToggle={handleToggle}
        />
      </div>

      {/* Compare button */}
      <Button
        onClick={handleCompare}
        disabled={selectedIds.length < 2 || computeMutation.isPending}
        data-testid="compare-button"
      >
        {computeMutation.isPending ? "Computing..." : "Compare"}
      </Button>

      {computeMutation.isError && (
        <p className="text-sm text-destructive">
          Failed to compute overlap: {computeMutation.error.message}
        </p>
      )}

      {/* Overlap results */}
      {overlapData && (
        <>
          <Separator />
          <OverlapStats data={overlapData} />
          <SharedPapersTable data={overlapData} />

          {/* Zero-overlap: explain rather than dead-end silently */}
          {overlapData.total_shared === 0 && (
            <div
              className="rounded-md border border-dashed bg-muted/30 p-4 text-sm text-muted-foreground"
              data-testid="no-overlap-note"
            >
              <p className="font-medium text-foreground">No shared papers.</p>
              <p className="mt-1">
                These concepts draw on distinct literature — there's nothing in
                common to bridge or analyze. Comparison is most useful for
                concepts you suspect overlap. Try selecting concepts in adjacent
                areas, or add more papers to each.
              </p>
            </div>
          )}

          {/* Analyze button — only if there are shared papers. The AI
              analysis is Pro; the structural Venn above stays free. */}
          {overlapData.total_shared > 0 &&
            (currentUser && !paid ? (
              <UpgradeCard
                feature="Overlap Analysis"
                description="Let AI surface cross-concept connections, bridge papers, and gaps from your shared papers."
              />
            ) : (
              <>
                <Button
                  onClick={handleAnalyze}
                  disabled={analyzeMutation.isPending}
                  variant="outline"
                  data-testid="analyze-button"
                >
                  {analyzeMutation.isPending
                    ? "Analyzing..."
                    : "Analyze Connections"}
                </Button>

                {analyzeMutation.isError && (
                  <p className="text-sm text-destructive">
                    Analysis failed: {analyzeMutation.error.message}
                  </p>
                )}
              </>
            ))}
        </>
      )}

      {/* Analysis results */}
      {analysisData && (
        <>
          <Separator />
          <OverlapAnalysisPanel data={analysisData} />
        </>
      )}
    </div>
  );
}
